import { TuningTrialError } from '@playcastdotio/PeerCore';
import type { ITrialReplacementEndpoint, ITuningClock, ResolvedTuningConfiguration, TrialFirstFrameEvidence,
  TuningCommand, TuningIdentity, TuningPrepared, TuningResume, TuningTrialControlContext, TuningWireMessage } from '@playcastdotio/PeerCore';
type Pending<T> = { result: Promise<T>; resolve(value: T): void; reject(error: Error): void };
type RemoteTrial = { context: TuningTrialControlContext; prepared: Pending<void>; firstFrame: Pending<TrialFirstFrameEvidence> };

/** Keeps authenticated transaction state alive independently of either media data channel. */
export class RemoteTrialEndpoint implements ITrialReplacementEndpoint {
  private current: RemoteTrial | undefined;
  private settings: TuningResume['settings'] = [];
  constructor(private readonly options: { clock: ITuningClock; send(message: TuningWireMessage): boolean }) {}
  prepare(command: TuningCommand, target: TuningIdentity, configuration: ResolvedTuningConfiguration, deadlineHostMs: number): Promise<void> {
    this.rejectCurrent('superseded');
    const context = { target, commandId: command.commandId, leaseId: command.leaseId, configurationHash: configuration.configurationHash,
      strategyId: configuration.strategyId, deadlineHostMs };
    const trial = { context, prepared: this.pending<void>(), firstFrame: this.pending<TrialFirstFrameEvidence>() };
    this.current = trial;
    if (!this.options.send({ type: 'stream-tuning', version: 1, kind: 'prepare', payload: { ...context, resolvedConfiguration: configuration } })) {
      this.rejectCurrent('control-unavailable');
    }
    return trial.prepared.result;
  }
  async replace(_command: TuningCommand, next: TuningIdentity, _configuration: ResolvedTuningConfiguration): Promise<void> {
    const current = this.forTarget(next); await current.prepared.result;
    if (!this.options.send({ type: 'stream-tuning', version: 1, kind: 'commit', payload: current.context })) {
      throw new TuningTrialError('control-unavailable', 'Authenticated commit transport unavailable');
    }
  }
  firstFrame(next: TuningIdentity): Promise<TrialFirstFrameEvidence> { return this.forTarget(next).firstFrame.result; }
  async abort(next: TuningIdentity, reason: string): Promise<void> {
    const current = this.current;
    if (!current || !this.sameTarget(current.context.target, next)) return;
    this.options.send({ type: 'stream-tuning', version: 1, kind: 'abort', payload: { ...current.context, reason: reason.slice(0, 2_048) } });
    this.rejectCurrent(reason); this.current = undefined;
  }
  accept(message: TuningWireMessage): boolean {
    if (message.kind !== 'prepared' && message.kind !== 'resume') return false;
    if (!this.current || !this.matches(message.payload, this.current.context)) return false;
    if (message.kind === 'prepared') this.acceptPrepared(message.payload);
    else this.acceptResume(message.payload);
    return true;
  }
  context(): TuningTrialControlContext | undefined { return this.current && structuredClone(this.current.context); }
  readback(): TuningResume['settings'] { return structuredClone(this.settings); }
  dispose(): void { this.rejectCurrent('disposed'); this.current = undefined; }
  private acceptPrepared(prepared: TuningPrepared): void {
    const current = this.current; if (!current) return;
    this.settings = prepared.settings;
    if (prepared.accepted) current.prepared.resolve(); else current.prepared.reject(new TuningTrialError('guest-preparation-rejected', prepared.error?.message ?? 'Guest rejected preparation'));
  }
  private acceptResume(resumed: TuningResume): void {
    const current = this.current; if (!current) return;
    this.settings = resumed.settings;
    if (resumed.stage === 'first-frame' && resumed.firstFrame) {
      current.firstFrame.resolve({ ...resumed.firstFrame, hostAckReceivedMonotonicMs: this.options.clock.monotonicMs() });
    }
  }
  private forTarget(target: TuningIdentity): RemoteTrial {
    if (!this.current || !this.sameTarget(this.current.context.target, target)) throw new TuningTrialError('stale-target', 'Replacement target is not prepared');
    return this.current;
  }
  private rejectCurrent(reason: string): void {
    const error = new TuningTrialError('trial-aborted', reason);
    this.current?.prepared.reject(error); this.current?.firstFrame.reject(error);
  }
  private pending<T>(): Pending<T> {
    let resolve!: (value: T) => void, reject!: (error: Error) => void;
    const result = new Promise<T>((accept, fail) => { resolve = accept; reject = fail; });
    void result.catch(() => undefined); return { result, resolve, reject };
  }
  private sameTarget(left: TuningIdentity, right: TuningIdentity): boolean {
    return left.sessionId === right.sessionId && left.peerId === right.peerId && left.trialId === right.trialId
      && left.connectionEpoch === right.connectionEpoch && left.configRevision === right.configRevision;
  }
  private matches(left: TuningTrialControlContext, right: TuningTrialControlContext): boolean {
    return this.sameTarget(left.target, right.target) && left.commandId === right.commandId && left.leaseId === right.leaseId
      && left.configurationHash === right.configurationHash && left.strategyId === right.strategyId && left.deadlineHostMs === right.deadlineHostMs;
  }
}
